/**
 * Slack's keyboard navigation (docs/design/slack-gap.md): ⌥↑ / ⌥↓ previous / next conversation in sidebar order,
 * ⌥⇧↑ / ⌥⇧↓ previous / next unread conversation, Esc marks the current one read, ⌘⇧A All unreads, ⌘/ the shortcut
 * sheet — plus the All unreads list itself. The sidebar's sections (logic/sidebar) give the order: conversations in
 * collapsed sections are skipped by ⌥↑↓ (they aren't on screen) but not by the unread jumps.
 */
import type { MessageKey } from "@bundle/i18n";
import type { SidebarSection } from "./sidebar.ts";
import type { Route } from "./shell.ts";

/** One conversation in sidebar order. */
export type ConversationStop = {
  /** Sidebar row id (unique). */
  id: string;
  /** The conversation (a DM with an agent that has no DM yet is the person's DM route). */
  channelId?: string;
  userId?: string;
  unread: boolean;
  mentions: number;
  /** Its section is collapsed. */
  hidden: boolean;
};

/** Conversations (channels, DMs, agents) in sidebar order, each once. Pages in folders are left out. */
export function conversationOrder(sections: readonly SidebarSection[], collapsed: readonly string[]): ConversationStop[] {
  const out: ConversationStop[] = [];
  const seen = new Set<string>();
  for (const s of sections) {
    const hidden = collapsed.includes(s.id);
    for (const row of s.rows) {
      if (row.type === "page") continue;
      const key = row.type === "agent" ? (row.channelId ?? `user:${row.userId}`) : row.channelId;
      if (seen.has(key)) continue;
      seen.add(key);
      const stop: ConversationStop = { id: row.id, unread: row.unread, mentions: row.mentions, hidden };
      if (row.type === "agent") {
        stop.userId = row.userId;
        if (row.channelId !== undefined) stop.channelId = row.channelId;
      } else stop.channelId = row.channelId;
      out.push(stop);
    }
  }
  return out;
}

/** Whether the route shows this conversation. */
export function isAt(stop: ConversationStop, route: Route | undefined): boolean {
  if (route === undefined) return false;
  if ((route.type === "channel" || route.type === "thread") && stop.channelId !== undefined) return route.channelId === stop.channelId;
  if (route.type === "dm" && stop.userId !== undefined) return route.userId === stop.userId;
  return false;
}

/**
 * The conversation ⌥↑ (`dir` -1) / ⌥↓ (+1) moves to — or, `unreadOnly`, ⌥⇧↑ / ⌥⇧↓ the previous / next unread one.
 * From outside the list it starts at the end (↑) or the start (↓). No wrap-around (Slack stops at the ends).
 */
export function adjacentConversation(order: readonly ConversationStop[], current: Route | undefined, dir: 1 | -1, unreadOnly: boolean): ConversationStop | undefined {
  const at = order.findIndex((s) => isAt(s, current));
  const ok = (s: ConversationStop) => (unreadOnly ? s.unread || s.mentions > 0 : !s.hidden);
  let i = at < 0 ? (dir > 0 ? -1 : order.length) : at;
  for (;;) {
    i += dir;
    const s = order[i];
    if (s === undefined) return undefined;
    if (!isAt(s, current) && ok(s)) return s;
  }
}

/** The route a stop opens. */
export function stopRoute(stop: ConversationStop, ws: string): Route {
  return stop.channelId !== undefined ? { type: "channel", ws, channelId: stop.channelId } : { type: "dm", ws, userId: stop.userId ?? "" };
}

/** All unreads: unread conversations (or with unread mentions), in sidebar order. */
export function unreadStops(order: readonly ConversationStop[]): ConversationStop[] {
  return order.filter((s) => s.channelId !== undefined && (s.unread || s.mentions > 0));
}

/** All unreads shows at most this many of a conversation's newest unread messages (Slack shows the latest few). */
export const UNREAD_PREVIEW_MAX = 3;

/**
 * A conversation's unread messages for All unreads: channel-level ones (top-level, or thread replies also sent to the
 * channel) after my read marker, not deleted, not mine — the newest `max`, oldest first — and how many more unread
 * there are (`unreadCount` = lastMessageSeq - lastReadSeq counts them all).
 */
export function unreadPreview<M extends { id: string; seq: number; authorId: string; deleted?: boolean | undefined; threadRootId?: string | undefined; alsoSentToChannel?: boolean | undefined }>(
  messages: readonly M[],
  lastReadSeq: number,
  meId: string,
  unreadCount: number,
  max: number = UNREAD_PREVIEW_MAX,
): { messages: M[]; more: number } {
  const unread = messages
    .filter((m) => m.seq > lastReadSeq && m.deleted !== true && m.authorId !== meId && (m.threadRootId === undefined || m.alsoSentToChannel === true))
    .toSorted((a, b) => a.seq - b.seq);
  const shown = unread.slice(Math.max(0, unread.length - max));
  return { messages: shown, more: Math.max(0, unreadCount - shown.length) };
}

/** The shortcut sheet (⌘/): keys per platform are the UI's (`mod` = ⌘ on Apple, Ctrl elsewhere). */
export const SHORTCUTS: readonly { id: string; keys: readonly ("mod" | "alt" | "shift" | string)[]; label: MessageKey }[] = [
  { id: "omnibox", keys: ["mod", "K"], label: "shortcuts.omnibox" },
  { id: "prev", keys: ["alt", "↑"], label: "shortcuts.prev" },
  { id: "next", keys: ["alt", "↓"], label: "shortcuts.next" },
  { id: "prevUnread", keys: ["alt", "shift", "↑"], label: "shortcuts.prevUnread" },
  { id: "nextUnread", keys: ["alt", "shift", "↓"], label: "shortcuts.nextUnread" },
  { id: "markRead", keys: ["Esc"], label: "shortcuts.markRead" },
  { id: "allUnreads", keys: ["mod", "shift", "A"], label: "shortcuts.allUnreads" },
  { id: "editLast", keys: ["↑"], label: "shortcuts.editLast" },
  { id: "newTab", keys: ["mod", "T"], label: "shortcuts.newTab" },
  { id: "closeTab", keys: ["mod", "W"], label: "shortcuts.closeTab" },
  { id: "tabSearch", keys: ["alt", "shift", "A"], label: "shortcuts.tabSearch" },
  { id: "split", keys: ["mod", "\\"], label: "shortcuts.split" },
  { id: "swapSplit", keys: ["mod", "alt", "\\"], label: "shortcuts.swapSplit" },
  { id: "back", keys: ["mod", "["], label: "shortcuts.back" },
  { id: "forward", keys: ["mod", "]"], label: "shortcuts.forward" },
  { id: "sidebar", keys: ["mod", "shift", "S"], label: "shortcuts.sidebar" },
  { id: "views", keys: ["mod", "alt", "1–3"], label: "shortcuts.views" },
  { id: "help", keys: ["mod", "/"], label: "shortcuts.help" },
];

/** Key caps for display: Apple uses ⌘ ⌥ ⇧, others Ctrl Alt Shift. */
export function keyCaps(keys: readonly string[], apple: boolean): string[] {
  return keys.map((k) => (k === "mod" ? (apple ? "⌘" : "Ctrl") : k === "alt" ? (apple ? "⌥" : "Alt") : k === "shift" ? (apple ? "⇧" : "Shift") : k));
}

/** Gmail's single-key mail shortcuts (on in Mail when focus isn't in a field). */
export type MailShortcut =
  | "compose" | "search" | "next" | "prev" | "open" | "archive" | "trash" | "spam" | "star" | "priority" | "notPriority" | "select" | "markRead" | "markUnread"
  | "reply" | "replyAll" | "forward";

export const MAIL_SHORTCUTS: readonly { id: MailShortcut; keys: readonly string[]; label: MessageKey }[] = [
  { id: "compose", keys: ["C"], label: "shortcuts.mail.compose" },
  { id: "search", keys: ["/"], label: "shortcuts.mail.search" },
  { id: "next", keys: ["J"], label: "shortcuts.mail.next" },
  { id: "prev", keys: ["K"], label: "shortcuts.mail.prev" },
  { id: "open", keys: ["O"], label: "shortcuts.mail.open" },
  { id: "archive", keys: ["E"], label: "shortcuts.mail.archive" },
  { id: "trash", keys: ["#"], label: "shortcuts.mail.trash" },
  { id: "spam", keys: ["!"], label: "shortcuts.mail.spam" },
  { id: "star", keys: ["S"], label: "shortcuts.mail.star" },
  { id: "priority", keys: ["+"], label: "shortcuts.mail.priority" },
  { id: "notPriority", keys: ["−"], label: "shortcuts.mail.notPriority" },
  { id: "select", keys: ["X"], label: "shortcuts.mail.select" },
  { id: "markRead", keys: ["shift", "I"], label: "shortcuts.mail.markRead" },
  { id: "markUnread", keys: ["shift", "U"], label: "shortcuts.mail.markUnread" },
  { id: "reply", keys: ["R"], label: "shortcuts.mail.reply" },
  { id: "replyAll", keys: ["A"], label: "shortcuts.mail.replyAll" },
  { id: "forward", keys: ["F"], label: "shortcuts.mail.forward" },
];

/** The mail shortcut a key press means (`key` as KeyboardEvent.key), or undefined. Modifiers other than Shift: none. */
export function mailShortcut(key: string, shift: boolean): MailShortcut | undefined {
  if (key === "Enter") return "open";
  if (key === "#") return "trash";
  if (key === "!") return "spam";
  if (key === "/") return "search";
  // Gmail's importance keys: + (or =) and −.
  if (key === "+" || key === "=") return "priority";
  if (key === "-") return "notPriority";
  const k = key.toLowerCase();
  if (shift) return k === "i" ? "markRead" : k === "u" ? "markUnread" : undefined;
  const map: Record<string, MailShortcut> = { c: "compose", j: "next", k: "prev", o: "open", e: "archive", s: "star", x: "select", r: "reply", a: "replyAll", f: "forward" };
  return map[k];
}
